// Billing / license (Stripe + IAP) RPC facades (knowledge/87 Phase 2).
// Extracted verbatim from app.svelte.ts — these are pure async client.* facades
// (plus reactive mutations of the shared licenseState/workspaceState proxies,
// which stay reactive from a plain .ts). No $state/$derived/$effect runes live
// here, so this is a plain .ts, NOT .svelte.ts. app.svelte.ts re-exports every
// symbol below so importers (Plans/License panels, LicenseAllocationPanel,
// billing return route) never change.
import { toast } from "svelte-sonner";
import { client } from "./client.js";
import { workspaceState } from "../core/state.svelte.js";
import { licenseState } from "./license.svelte.js";
import { isTauriIOS, isTauriAndroid } from "../mobile/push.js";
import { isDesktopApp } from "../utils.js";
import type { BillingPlatformPayload } from "../core/client.js";
import type { BillingDetailsResponse } from "../ws-client-types.js";
import { reportUiFailure, type ReportOptions } from "../report-failure.js";
import { isUnsupportedByRelay } from "./thread-sync.js";
import { openExternalUrl } from "../desktop-terminal.js";

// ─── Billing / license (Stripe) ───────────────────────────────────

// Pull the workspace's authoritative license into licenseState. Best-effort:
// an older relay (or self-hosted with no billing) just leaves the bar hidden.
// iOS self-heal guard: reconcile a paused license against RevenueCat at most
// once per workspace per session, so a missed webhook recovers without
// hammering the reconcile endpoint on every refresh.
const iapReconciledWorkspaces = new Set<string>();

// The caller's billing surface, sent with fetchLicense so the relay resolves the
// right intent (web/desktop → Stripe rail, ios/android → IAP rail). iOS/Android
// run inside Tauri; desktop is the Electron shell; everything else is the web.
export function currentBillingPlatform(): BillingPlatformPayload {
  if (isTauriIOS()) return "ios";
  if (isTauriAndroid()) return "android";
  if (isDesktopApp()) return "desktop";
  return "web";
}

export function startLicenseRead(wsId: string): ReturnType<typeof client.fetchLicense> {
  return client.fetchLicense(wsId, currentBillingPlatform());
}

export async function refreshLicense(
  workspaceId?: string,
  read?: ReturnType<typeof client.fetchLicense>,
  isCurrent?: () => boolean,
): Promise<void> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) return;
  try {
    const fetched = await (read ?? startLicenseRead(wsId));
    let license = fetched.license;
    let intent = fetched.intent;
    // On iOS, a `paused` license may just be a RevenueCat webhook we never
    // received (downtime / auth not yet deployed). Reconcile once against
    // RevenueCat and adopt the corrected result before showing the locked state.
    // (reconcileIap returns only the license; the intent is re-derived on the
    // next fetch — until then we drop the stale intent so the UI doesn't show a
    // purchase card for a now-active license.)
    if (license.state === "paused" && isTauriIOS() && !iapReconciledWorkspaces.has(wsId)) {
      iapReconciledWorkspaces.add(wsId);
      const reconciled = await client.reconcileIap(wsId);
      if (reconciled.ok && reconciled.license) {
        license = reconciled.license;
        intent = null;
      }
    }
    // Don't clobber a newer selection's license with a stale fetch.
    if (isCurrent ? isCurrent() : workspaceState.current?.id === wsId) {
      licenseState.set(license);
      licenseState.setIntent(intent);
    }
  } catch (err) {
    // An older relay without the billing route refuses ("unsupported: ..."): the trial bar stays hidden.
    const options: ReportOptions = isUnsupportedByRelay(err)
      ? { code: "unsupported", failureKind: "refusal" }
      : {};
    reportUiFailure("billing.refresh_license", err, { workspaceId: wsId }, options);
  }
}

// iOS only: reconcile the caller's RevenueCat entitlement on the relay and adopt
// the resulting license. Called right after a StoreKit purchase/restore so
// access reflects immediately (the webhook is async). A failed or empty result leaves
// the optimistic state in place until the webhook lands (reconcileIap reported why).
export async function reconcileIapLicense(): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId) return;
  const reconciled = await client.reconcileIap(wsId);
  if (reconciled.ok && reconciled.license && workspaceState.current?.id === wsId) {
    licenseState.set(reconciled.license);
    iapReconciledWorkspaces.add(wsId);
  }
}

// Owner-only: start a Stripe Checkout Session. Card entry happens on
// checkout.stripe.com — never in our UI.
//
// Web: redirect the tab to the hosted page (`window.location.href`). The
// Stripe success_url returns to the app, so the page leaving IS the flow.
//
// Desktop (Electron): the main process denies in-place navigation off the app
// origin (main.ts `will-navigate`), so `location.href` would silently stick.
// Open the hosted page in the OS browser via the `openExternal` bridge instead,
// and return true so the caller can switch to a "waiting for activation" state
// and poll the license (the external tab can't redirect back into the app).
//
// Returns true when it opened externally (desktop), false when it navigated the
// current tab (web).
export async function startCheckout(interval: "month" | "year" = "month"): Promise<boolean> {
  const wsId = workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const url = await client.startCheckout(wsId, interval);
  if (isDesktopApp()) {
    openExternalUrl(url);
    return true;
  }
  if (typeof window !== "undefined") window.location.href = url;
  return false;
}

// Owner-only (active subscription): open the Stripe billing portal.
//
// Web: redirect the current tab (the portal's return URL brings the user back).
// Desktop: in-place navigation off the app origin is denied (see startCheckout),
// so open the portal in the OS browser instead. Returns true when it opened
// externally (desktop) so a caller with a loading state can reset it.
export async function openBillingPortal(): Promise<boolean> {
  const wsId = workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const url = await client.openBillingPortal(wsId);
  if (isDesktopApp()) {
    openExternalUrl(url);
    return true;
  }
  if (typeof window !== "undefined") window.location.href = url;
  return false;
}

// Owner-only card + invoices for the billing page. Null on a refusal or an older relay
// (reported here); a Stripe outage comes back as a result with null invoices, already
// reported by the relay. Either way the page keeps its plain copy + portal links.
export async function fetchBillingDetails(
  workspaceId: string,
): Promise<BillingDetailsResponse | null> {
  try {
    return await client.getBillingDetails(workspaceId);
  } catch (err) {
    reportUiFailure("billing.details", err, { workspaceId });
    // intentional: reported above; absence makes the page keep its plain copy + portal links
    return null; // null: "no details", the page's fallback state
  }
}

// Handle the post-Checkout return query param (?billing=success|canceled).
// success → re-fetch the license (the webhook may already have flipped it to
// active) + toast; canceled → just close the modal. The caller cleans the param.
export async function handleBillingReturn(status: string | null): Promise<void> {
  if (status === "success") {
    await refreshLicense();
    licenseState.modalOpen = false;
    toast.success("License activated — your agents are back online.");
  } else if (status === "canceled") {
    licenseState.modalOpen = false;
  }
}

// ─── License pool + seat allocation (per-workspace billing, spec §4.3) ────────
// The LicenseAllocationPanel reads/mutates the caller's seat pool through these.
// They round-trip the `cyborg:fetch_license_pool` / `allocate_license` /
// `deallocate_license` WS RPCs (Unit E handlers in relay-standalone.ts).
//
// On a transport/timeout failure (thrown): log + leave state unchanged (the panel
// retains whatever it last showed; a stale view is better than a misleading one).
// On a business rejection (resp.error set — no_free_seat / not_owner / no_pool /
// already_active_other_rail): do NOT mutate optimistic state; return the code so
// the panel can surface the reason inline.

// Fetch the caller's seat pool + allocations + owned workspaces into licenseState.
export async function fetchLicensePool(): Promise<void> {
  licenseState.poolLoading = true;
  try {
    const resp = await client.fetchLicensePool();
    licenseState.setPool(resp.pool, resp.allocations, resp.ownedWorkspaces);
  } catch (err) {
    console.error(
      "[billing] cyborg:fetch_license_pool failed — leaving state unchanged:",
      err instanceof Error ? err.message : String(err),
    );
  } finally {
    licenseState.poolLoading = false;
  }
}

// Owner-only: spend one FREE seat on `workspaceId` (no purchase). Adopts the
// relay's recomputed pool + allocations, then refreshes the gate mirror for the
// active workspace. Returns the server error code (e.g. "no_free_seat") on a
// rejected mutation, or null on success — the panel branches on it.
// On a transport/timeout failure (thrown): logs + re-throws so the panel can
// surface a generic "try again" message (state left unchanged).
export async function allocateLicense(workspaceId: string): Promise<string | null> {
  licenseState.poolLoading = true;
  try {
    const resp = await client.allocateLicense(workspaceId);
    if (resp.error) return resp.error;
    licenseState.setPoolAndAllocations(resp.pool, resp.allocations);
    // Adopt the fresh license only for the workspace on screen (spec §4.3).
    if (workspaceState.current?.id === workspaceId) licenseState.set(resp.license);
    return null;
  } catch (err) {
    console.error(
      "[billing] cyborg:allocate_license failed:",
      err instanceof Error ? err.message : String(err),
    );
    return "error";
  } finally {
    licenseState.poolLoading = false;
  }
}

// Owner-only: free the seat on `workspaceId`. Same response handling as allocate.
export async function deallocateLicense(workspaceId: string): Promise<string | null> {
  licenseState.poolLoading = true;
  try {
    const resp = await client.deallocateLicense(workspaceId);
    if (resp.error) return resp.error;
    licenseState.setPoolAndAllocations(resp.pool, resp.allocations);
    if (workspaceState.current?.id === workspaceId) licenseState.set(resp.license);
    return null;
  } catch (err) {
    console.error(
      "[billing] cyborg:deallocate_license failed:",
      err instanceof Error ? err.message : String(err),
    );
    return "error";
  } finally {
    licenseState.poolLoading = false;
  }
}
